import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiTrash2, FiAlertCircle, FiDollarSign } from 'react-icons/fi';
import { getMemberOutstanding, addMemberOutstanding, deleteOutstandingEntry, settleOutstandingManual } from '../services/api';

// v1.6.0 — Outstanding-debt card. Shown on both admin MemberProfile and
// portal MyProfile (readonly variant). Admin/cashier get Add + Manual settle;
// members just see the banner + entry list.
//
// Props:
//   memberId    — REQUIRED. The member to load.
//   readOnly    — true for the member-portal side (no add/settle buttons).
//   compact     — true renders as a slim yellow banner instead of full card.
//   currencies  — optional list from Church Profile; defaults ['$','K'].
//   onChange    — optional callback fired after any mutation, so the parent
//                 profile page can re-fetch itself.
const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const OutstandingBalance = ({ memberId, readOnly = false, compact = false, currencies = ['$', 'K'], onChange }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [balance, setBalance] = useState({ usd: 0, kwacha: 0, entries: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showSettle, setShowSettle] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [form, setForm] = useState({ amount_usd: '', amount_kwacha: '', description: '' });
  const [settleForm, setSettleForm] = useState({ amount_usd: '', amount_kwacha: '', note: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!memberId) return;
    setLoading(true);
    getMemberOutstanding(memberId, true).then(r => {
      setEntries(Array.isArray(r.data?.entries) ? r.data.entries : []);
      setBalance(r.data?.balance || { usd: 0, kwacha: 0, entries: 0 });
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  const openEntries = entries.filter(e => !e.settled);
  const settledCount = entries.length - openEntries.length;

  const handleAdd = async () => {
    setError('');
    const usd = parseFloat(form.amount_usd || 0) || 0;
    const k   = parseFloat(form.amount_kwacha || 0) || 0;
    if (usd <= 0 && k <= 0) return setError(t('outstanding.amountRequired', { defaultValue: 'Enter at least one amount' }));
    setBusy(true);
    try {
      await addMemberOutstanding(memberId, { amount_usd: usd, amount_kwacha: k, description: form.description });
      setShowAdd(false);
      setForm({ amount_usd: '', amount_kwacha: '', description: '' });
      load();
      onChange && onChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const handleSettle = async () => {
    setError('');
    const usd = parseFloat(settleForm.amount_usd || 0) || 0;
    const k   = parseFloat(settleForm.amount_kwacha || 0) || 0;
    if (usd <= 0 && k <= 0) return setError(t('outstanding.amountRequired', { defaultValue: 'Enter at least one amount' }));
    if (!settleForm.note.trim()) return setError(t('outstanding.noteRequired', { defaultValue: 'A note is required' }));
    setBusy(true);
    try {
      await settleOutstandingManual(memberId, { amount_usd: usd, amount_kwacha: k, note: settleForm.note });
      setShowSettle(false);
      setSettleForm({ amount_usd: '', amount_kwacha: '', note: '' });
      load();
      onChange && onChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm(t('outstanding.deleteConfirm', { defaultValue: 'Delete this outstanding entry? This cannot be undone.' }))) return;
    try {
      await deleteOutstandingEntry(entryId);
      load();
      onChange && onChange();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const hasBalance = balance.usd > 0 || balance.kwacha > 0;

  // Compact banner-only variant (for tight header spots on portal). Doesn't
  // list entries or show admin buttons — click it to expand.
  if (compact) {
    if (loading || !hasBalance) return null;
    return (
      <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiAlertCircle style={{ color: '#b45309', flexShrink: 0 }} size={20} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>
            {t('outstanding.bannerTitle', { defaultValue: 'You have an outstanding balance' })}
          </div>
          <div style={{ color: '#78350f', fontSize: 13, marginTop: 2 }}>
            {balance.usd > 0 && <>${fmt(balance.usd)}</>}
            {balance.usd > 0 && balance.kwacha > 0 && ' · '}
            {balance.kwacha > 0 && <>K{fmt(balance.kwacha)}</>}
            {' · '}
            {t('outstanding.pleaseSettle', { defaultValue: 'please see the treasurer to settle' })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 16, border: hasBalance ? '2px solid #fde68a' : undefined }}>
      <div className="card-header" style={{ background: hasBalance ? '#fffbeb' : undefined, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiDollarSign style={{ color: hasBalance ? '#b45309' : '#6b7280' }} />
          <span style={{ fontWeight: 700 }}>{t('outstanding.title', { defaultValue: 'Outstanding balance (legacy debt)' })}</span>
          {hasBalance && (
            <span className="pill pill-yellow" style={{ marginLeft: 6 }}>
              {balance.usd > 0 && `$${fmt(balance.usd)}`}
              {balance.usd > 0 && balance.kwacha > 0 && ' · '}
              {balance.kwacha > 0 && `K${fmt(balance.kwacha)}`}
            </span>
          )}
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', gap: 6 }}>
            {hasBalance && <button className="btn btn-sm btn-success" onClick={() => setShowSettle(true)}>{t('outstanding.markSettled', { defaultValue: 'Mark settled' })}</button>}
            <button className="btn btn-sm btn-primary" onClick={() => setShowAdd(true)}><FiPlus /> {t('outstanding.addBtn', { defaultValue: 'Add' })}</button>
          </div>
        )}
      </div>

      <div className="card-body">
        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>{t('common.loading', { defaultValue: 'Loading…' })}</p>
        ) : entries.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>{t('outstanding.noneYet', { defaultValue: 'No outstanding balance recorded.' })}</p>
        ) : (
          <>
            {openEntries.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {openEntries.map(e => (
                  <div key={e.id} style={{ background: '#fffbeb', border: '1px solid #fef3c7', padding: '10px 12px', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#78350f' }}>
                          {e.remaining_usd > 0 && `$${fmt(e.remaining_usd)}`}
                          {e.remaining_usd > 0 && e.remaining_kwacha > 0 && ' · '}
                          {e.remaining_kwacha > 0 && `K${fmt(e.remaining_kwacha)}`}
                          {(e.paid_usd > 0 || e.paid_kwacha > 0) && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#92400e', fontWeight: 500 }}>
                              (paid: {e.paid_usd > 0 && `$${fmt(e.paid_usd)}`}{e.paid_usd > 0 && e.paid_kwacha > 0 && ', '}{e.paid_kwacha > 0 && `K${fmt(e.paid_kwacha)}`})
                            </span>
                          )}
                        </div>
                        {e.description && <div style={{ fontSize: 12, color: '#78350f', marginTop: 2 }}>{e.description}</div>}
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                          {e.entered_at?.slice(0, 10)} · {e.entered_by_name || '—'}
                        </div>
                      </div>
                      {!readOnly && e.paid_usd === 0 && e.paid_kwacha === 0 && (
                        <button className="btn-icon danger" onClick={() => handleDelete(e.id)} title={t('common.delete', { defaultValue: 'Delete' })}>
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {settledCount > 0 && (
              <div style={{ marginTop: openEntries.length > 0 ? 12 : 0 }}>
                <button
                  onClick={() => setShowHistory(s => !s)}
                  style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer', padding: 0 }}
                >
                  {showHistory ? '▾' : '▸'} {settledCount} {t('outstanding.settledEntry', { defaultValue: 'settled entry' })}{settledCount !== 1 ? 's' : ''}
                </button>
                {showHistory && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {entries.filter(e => e.settled).map(e => (
                      <div key={e.id} style={{ background: '#f3f4f6', padding: '8px 10px', borderRadius: 6, fontSize: 12, color: '#4b5563' }}>
                        <span style={{ textDecoration: 'line-through' }}>
                          {parseFloat(e.amount_usd) > 0 && `$${fmt(e.amount_usd)}`}
                          {parseFloat(e.amount_usd) > 0 && parseFloat(e.amount_kwacha) > 0 && ' · '}
                          {parseFloat(e.amount_kwacha) > 0 && `K${fmt(e.amount_kwacha)}`}
                        </span>
                        {e.description && <> — {e.description}</>}
                        <span style={{ marginLeft: 6, color: '#9ca3af' }}>({e.entered_at?.slice(0, 10)})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add entry modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => !busy && setShowAdd(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('outstanding.addTitle', { defaultValue: 'Add outstanding balance' })}</h3>
              <button className="btn-icon" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                {currencies.includes('$') && (
                  <div className="form-group">
                    <label>Amount ($)</label>
                    <input type="number" step="0.01" value={form.amount_usd}
                      onChange={e => setForm(f => ({ ...f, amount_usd: e.target.value }))} placeholder="0.00" />
                  </div>
                )}
                {currencies.includes('K') && (
                  <div className="form-group">
                    <label>Amount (K)</label>
                    <input type="number" step="0.01" value={form.amount_kwacha}
                      onChange={e => setForm(f => ({ ...f, amount_kwacha: e.target.value }))} placeholder="0.00" />
                  </div>
                )}
                <div className="form-group form-full">
                  <label>{t('outstanding.descriptionLabel', { defaultValue: 'Description / source' })}</label>
                  <input value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="e.g. Pre-2026 pledge — brought over from paper records" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)} disabled={busy}>{t('common.cancel', { defaultValue: 'Cancel' })}</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={busy}>{busy ? '…' : (t('common.save', { defaultValue: 'Save' }))}</button>
            </div>
          </div>
        </div>
      )}

      {/* Manual settle modal — 'paid outside the system' */}
      {showSettle && (
        <div className="modal-overlay" onClick={() => !busy && setShowSettle(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('outstanding.settleTitle', { defaultValue: 'Mark part or all as settled' })}</h3>
              <button className="btn-icon" onClick={() => setShowSettle(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
                {t('outstanding.settleHint', { defaultValue: 'Use this ONLY when payment was made outside the system (e.g. handed to the treasurer in cash). If the member paid via a Cash Receipt, use the CR form instead — it can settle this automatically.' })}
              </p>
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                {balance.usd > 0 && (
                  <div className="form-group">
                    <label>Amount ($) — owed ${fmt(balance.usd)}</label>
                    <input type="number" step="0.01" value={settleForm.amount_usd}
                      onChange={e => setSettleForm(f => ({ ...f, amount_usd: e.target.value }))} placeholder="0.00" />
                  </div>
                )}
                {balance.kwacha > 0 && (
                  <div className="form-group">
                    <label>Amount (K) — owed K{fmt(balance.kwacha)}</label>
                    <input type="number" step="0.01" value={settleForm.amount_kwacha}
                      onChange={e => setSettleForm(f => ({ ...f, amount_kwacha: e.target.value }))} placeholder="0.00" />
                  </div>
                )}
                <div className="form-group form-full">
                  <label>{t('outstanding.settleNote', { defaultValue: 'Note (required)' })}</label>
                  <input value={settleForm.note}
                    onChange={e => setSettleForm(f => ({ ...f, note: e.target.value }))}
                    placeholder="e.g. Paid $100 in cash on 2026-06-15, receipt in office" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowSettle(false)} disabled={busy}>{t('common.cancel', { defaultValue: 'Cancel' })}</button>
              <button className="btn btn-success" onClick={handleSettle} disabled={busy}>{busy ? '…' : (t('outstanding.markSettled', { defaultValue: 'Mark settled' }))}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutstandingBalance;
